import Lottie from 'lottie-react';
import { Crosshair } from 'lucide-react';
import { useMap } from 'react-naver-maps';
import pinAnimation from '@/assets/lottie/pin.json';
import {
	BOTTOM_MODAL_SHEET_COLLAPSED_HEIGHT,
	BOTTOM_SHEET_TOP_MARGIN,
	NAVIGATION_HEIGHT,
} from '@/shared/consts';
import { useBoundStore } from '@/shared/store';
import { focusMapPosition } from '../lib';
import useNaverMapStore from '../model/naverMapStore';

type NaverMapMyLocationButtonProps = {
	latitude?: number;
	longitude?: number;
	isLocationPermissionDenied?: boolean;
};

const PADDING = 20;

const NaverMapMyLocationButton = ({
	latitude,
	longitude,
	isLocationPermissionDenied,
}: NaverMapMyLocationButtonProps) => {
	const map = useMap();
	const minZoomLevel = useNaverMapStore((state) => state.minZoomLevel);
	const openModal = useBoundStore((state) => state.openModal);
	const closeModal = useBoundStore((state) => state.closeModal);
	const setSelectedPlace = useBoundStore((state) => state.setSelectedPlace);
	const currentSnapPoint = useBoundStore((store) => store.currentSnapPoint);
	const projection = map.getProjection();

	const handleMoveToMyLocation = () => {
		if (isLocationPermissionDenied || !latitude || !longitude) {
			openModal({
				title: '위치 허용이 필요해요',
				visual: (
					<Lottie
						animationData={pinAnimation}
						loop={false}
						initialSegment={[1, 92]}
					/>
				),
				children: (
					<div className="text-center">
						<div className="relative -top-4">
							만땅의 모든 기능을 사용하기 위해
							<br />
							위치 허용을 해주세요
						</div>
					</div>
				),
				primaryAction: {
					label: '닫기',
					onClick: () => closeModal(),
				},
			});
			return;
		}

		const position = new naver.maps.LatLng(latitude, longitude);

		map.setCenter(position);
		map.setZoom(minZoomLevel || 15, false);

		const mapElement = map.getElement?.() as HTMLElement | undefined;
		const mapRect = mapElement?.getBoundingClientRect();

		if (!mapRect) {
			map.panTo(position);
			return;
		}

		const sheetHeight =
			(currentSnapPoint ?? 0) >= 2
				? (window.innerHeight - BOTTOM_SHEET_TOP_MARGIN - NAVIGATION_HEIGHT) *
					0.5
				: BOTTOM_MODAL_SHEET_COLLAPSED_HEIGHT;
		const sheetTopFromScreenTop =
			window.innerHeight - NAVIGATION_HEIGHT - sheetHeight;
		const sheetTopWithinMap = Math.min(
			mapRect.height,
			Math.max(0, sheetTopFromScreenTop - mapRect.top),
		);
		const targetY = sheetTopWithinMap / 2;

		const currentOffset = projection.fromCoordToOffset(position);
		const viewportCenter = new naver.maps.Point(
			mapRect.width / 2,
			mapRect.height / 2,
		);
		const targetPoint = new naver.maps.Point(viewportCenter.x, targetY);
		const adjustedCenterOffset = new naver.maps.Point(
			currentOffset.x + viewportCenter.x - targetPoint.x,
			currentOffset.y + viewportCenter.y - targetPoint.y,
		);

		map.panTo(projection.fromOffsetToCoord(adjustedCenterOffset));
		setSelectedPlace(null);
		focusMapPosition({ map, position, targetY });
	};

	const bottom =
		(currentSnapPoint ?? 0) >= 2
			? 'calc(50dvh - 22px)'
			: `${BOTTOM_MODAL_SHEET_COLLAPSED_HEIGHT + PADDING}px`;

	return (
		<button
			type="button"
			className="absolute z-10 flex h-12 w-12 cursor-pointer items-center justify-center rounded-[50%] bg-mantang-white drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)]"
			style={{
				bottom,
				right: PADDING,
				transition: 'bottom 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
			}}
			onClick={handleMoveToMyLocation}
		>
			<Crosshair />
		</button>
	);
};

export default NaverMapMyLocationButton;
